import { X } from 'lucide-react';
import { LEVEL_INFO, ACTIVITY_SCORE_RULES, type Level } from '@/constants/levels';
import { comma } from '@/lib/format';
import { useScrollLock } from '@/hooks/useScrollLock';

interface LevelInfoModalProps {
  open: boolean;
  onClose: () => void;
  currentLevel?: number;
  activityScore?: number;
}

export function LevelInfoModal({ open, onClose, currentLevel, activityScore }: LevelInfoModalProps) {
  useScrollLock(open);

  if (!open) return null;
  const levels = (Object.keys(LEVEL_INFO) as unknown as string[])
    .map((k) => Number(k) as Level)
    .sort((a, b) => a - b);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold">레벨 안내</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-gray-100"
            aria-label="닫기"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {typeof activityScore === 'number' && (
          <div className="mb-4 rounded-lg bg-gray-50 p-3 text-sm">
            <span className="text-gray-600">현재 활동 점수</span>{' '}
            <b>{comma(activityScore)}점</b>
            {currentLevel ? <span className="ml-2 text-gray-500">· Lv.{currentLevel}</span> : null}
          </div>
        )}

        <h4 className="mb-2 text-sm font-semibold">활동 점수 기준</h4>
        <ul className="mb-5 space-y-1 text-sm text-gray-700">
          {ACTIVITY_SCORE_RULES.map((r) => (
            <li key={r.label} className="flex justify-between">
              <span>{r.label}</span>
              <span className="font-semibold text-[var(--color-primary)]">+{r.delta}점</span>
            </li>
          ))}
        </ul>

        <h4 className="mb-2 text-sm font-semibold">레벨 구간</h4>
        <ul className="divide-y divide-gray-100 text-sm">
          {levels.map((lv) => {
            const info = LEVEL_INFO[lv];
            const nextMin = LEVEL_INFO[(lv + 1) as Level]?.minScore;
            const range = nextMin ? `${comma(info.minScore)} ~ ${comma(nextMin - 1)}` : `${comma(info.minScore)} 이상`;
            const isCurrent = currentLevel === lv;
            return (
              <li
                key={lv}
                className={`flex items-center justify-between py-2 ${isCurrent ? 'font-semibold' : ''}`}
              >
                <span
                  className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
                  style={{ backgroundColor: info.color + '18', color: info.color }}
                >
                  {info.label}
                </span>
                <span className="text-gray-600">{range}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
